/**
 * features/hr/ClockingImport.tsx
 * ------------------------------------------------------------------
 * HR DOMAIN — the Clocking Upload screen. THE ONLY WAY punches enter
 * the portal.
 *
 * Route `#/hrClockingImport`, gated by canImportClocking: Makoni's
 * account (superAcademicAdmin) and system admin, and nobody else — not
 * HR, by the school's decision (Oct 2026). The Monday reminder's bell,
 * push and email all open this route directly.
 *
 * What is uploaded is the terminals' own monthly "Attendance Record"
 * export (.xls), one file per terminal — see lib/clockingGrid for the
 * format and why the two terminals' ID lists must be kept apart. Several
 * files go up together and are merged.
 *
 * Three steps on one page:
 *   1. FILES   — pick the exports; each is assigned a terminal, guessed
 *                from the file name ("July 2.xls" → terminal 2).
 *   2. PEOPLE  — every terminal user who punched is linked to a staff
 *                record, once. Links are saved (hrClockingLinks) and
 *                reused every week after; a name match is offered as a
 *                suggestion, never applied without a click. A terminal
 *                user who is not staff (a test card, a visitor) can be
 *                marked "not staff" and is skipped from then on.
 *   3. IMPORT  — staff-days are merged with what is already stored for
 *                those dates (so terminal 2 never wipes terminal 1's
 *                morning punch, and re-uploading a file changes nothing),
 *                then written to hrAttendance, and the upload is logged
 *                in hrClockingUploads for the reminder to read.
 *
 * Replaces the CSV importer (EmployeeNo/Date/Time/Direction), which no
 * terminal at the school actually produced.
 * ------------------------------------------------------------------ */
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Upload, ArrowLeft, CheckCircle2, AlertTriangle, Loader2, Clock, Link2, FileSpreadsheet, X, History,
} from 'lucide-react';
import {
  collection, doc, getDocs, limit, onSnapshot, orderBy, query, where, writeBatch,
} from 'firebase/firestore';
import { db, addDoc } from '@/lib/firebase';
import { stripUndefined } from '@/lib/sanitize';
import { GradientHero, SoftCard, StatChip, AccentButton, glassFieldClass } from '@/features/finance/ui';
import { SaveConfirmation } from '@/components/ui/SaveConfirmation';
import UploadReminderRecipients from '@/features/notifications/UploadReminderRecipients';
import { recordHrAudit } from '@/lib/hrAudit';
import { useAuth } from '@/contexts/AuthContext';
import { readFirstSheet } from '@/lib/readSheet';
import { UPLOAD_REMINDERS } from '@/lib/uploadReminders';
import { useClockingRules } from '@/features/hr/useClockingRules';
import {
  HR_COLLECTIONS, staffFullName, type HrAttendance, type HrStaff,
} from '@/lib/hr';
import {
  assembleGridDay, cleanTimes, clockingLinkId, isClockingGrid, parseClockingGrid, punchHHMM,
  suggestStaff, terminalFromFileName, type ClockingGrid,
} from '@/lib/clockingGrid';

export const CLOCKING_LINKS_COLLECTION = 'hrClockingLinks';
const UPLOADS_COLLECTION = UPLOAD_REMINDERS.clocking.uploadsCollection;
const IGNORE = '__ignore';

interface ClockingLink {
  id: string;
  terminal: string;
  deviceUserId: string;
  deviceName: string;
  /** null when the terminal user was marked "not staff". */
  staffId: string | null;
  updatedAt: string;
  updatedBy: string;
}

interface LoadedFile {
  name: string;
  terminal: string;
  grid: ClockingGrid;
}

interface UploadLog {
  id: string;
  uploadedAt: string;
  uploadedByName: string;
  periodStart: string;
  periodEnd: string;
  staffDays: number;
}

/** A terminal user who punched in the uploaded files. */
interface Person {
  key: string;
  terminal: string;
  deviceUserId: string;
  deviceName: string;
  punches: number;
}

interface DayWrite {
  id: string;
  staffId: string;
  date: string;
  record: Omit<HrAttendance, 'id'>;
  kind: 'new' | 'updated';
}

export default function ClockingImport({ onBack }: { onBack?: () => void }) {
  const { userData } = useAuth();
  const clockingRules = useClockingRules();
  const actor = { id: userData?.uid ?? 'unknown', email: userData?.email ?? null };
  const fileRef = useRef<HTMLInputElement>(null);

  const [staff, setStaff] = useState<HrStaff[]>([]);
  const [links, setLinks] = useState<Map<string, ClockingLink>>(new Map());
  const [lastUpload, setLastUpload] = useState<UploadLog | null>(null);
  const [files, setFiles] = useState<LoadedFile[]>([]);
  const [fileErrors, setFileErrors] = useState<string[]>([]);
  const [reading, setReading] = useState(false);
  const [choices, setChoices] = useState<Record<string, string>>({});
  const [savingLinks, setSavingLinks] = useState(false);
  const [existing, setExisting] = useState<Map<string, HrAttendance>>(new Map());
  const [existingLoaded, setExistingLoaded] = useState(false);
  const [importing, setImporting] = useState(false);
  const [saveModal, setSaveModal] = useState<{ show: boolean; status: 'saving' | 'success' | 'error'; message: string }>(
    { show: false, status: 'saving', message: '' },
  );

  useEffect(() => onSnapshot(collection(db, HR_COLLECTIONS.staff), (snap) => {
    setStaff(snap.docs.map((d) => ({ ...(d.data() as HrStaff), id: d.id })));
  }), []);
  useEffect(() => onSnapshot(collection(db, CLOCKING_LINKS_COLLECTION), (snap) => {
    const m = new Map<string, ClockingLink>();
    snap.docs.forEach((d) => m.set(d.id, { ...(d.data() as ClockingLink), id: d.id }));
    setLinks(m);
  }), []);
  useEffect(() => onSnapshot(
    query(collection(db, UPLOADS_COLLECTION), orderBy('uploadedAt', 'desc'), limit(1)),
    (snap) => setLastUpload(snap.empty ? null : ({ ...(snap.docs[0].data() as UploadLog), id: snap.docs[0].id })),
    () => setLastUpload(null),
  ), []);

  const activeStaff = useMemo(
    () => staff.filter((s) => s.status !== 'exited').sort((a, b) => staffFullName(a).localeCompare(staffFullName(b))),
    [staff],
  );
  const staffById = useMemo(() => new Map(staff.map((s) => [s.id, s])), [staff]);

  /* ---------------- 1. files ---------------- */

  async function onPick(e: React.ChangeEvent<HTMLInputElement>) {
    const picked = Array.from(e.target.files ?? []);
    if (fileRef.current) fileRef.current.value = '';
    if (!picked.length) return;
    setReading(true);
    const loaded: LoadedFile[] = [];
    const errors: string[] = [];
    for (const f of picked) {
      try {
        const rows = await readFirstSheet(f);
        if (!isClockingGrid(rows)) throw new Error('not a terminal "Attendance Record" export');
        loaded.push({ name: f.name, terminal: terminalFromFileName(f.name), grid: parseClockingGrid(rows) });
      } catch (err) {
        errors.push(`${f.name}: ${(err as Error).message}`);
      }
    }
    setFiles((prev) => [...prev.filter((p) => !loaded.some((l) => l.name === p.name)), ...loaded]);
    setFileErrors(errors);
    setReading(false);
  }

  const period = useMemo(() => {
    if (!files.length) return null;
    return {
      start: files.map((f) => f.grid.periodStart).sort()[0],
      end: files.map((f) => f.grid.periodEnd).sort().reverse()[0],
    };
  }, [files]);

  // What is already stored for the period — the merge base.
  useEffect(() => {
    setExistingLoaded(false);
    if (!period) { setExisting(new Map()); return; }
    let live = true;
    getDocs(query(
      collection(db, HR_COLLECTIONS.attendance),
      where('date', '>=', period.start),
      where('date', '<=', period.end),
    )).then((snap) => {
      if (!live) return;
      const m = new Map<string, HrAttendance>();
      snap.docs.forEach((d) => m.set(d.id, { ...(d.data() as HrAttendance), id: d.id }));
      setExisting(m);
      setExistingLoaded(true);
    }).catch((err) => {
      if (live) setFileErrors((prev) => [...prev, `Could not read existing clocking: ${(err as Error).message}`]);
    });
    return () => { live = false; };
  }, [period]);

  /* ---------------- 2. people ---------------- */

  const people = useMemo<Person[]>(() => {
    const m = new Map<string, Person>();
    for (const f of files) {
      for (const p of f.grid.people) {
        const punches = Object.values(p.days).reduce((a, t) => a + t.length, 0);
        if (!punches) continue;
        const key = clockingLinkId(f.terminal, p.deviceUserId);
        const prev = m.get(key);
        if (prev) prev.punches += punches;
        else m.set(key, { key, terminal: f.terminal, deviceUserId: p.deviceUserId, deviceName: p.deviceName, punches });
      }
    }
    return Array.from(m.values());
  }, [files]);

  // Prefill each person's choice: their saved link, else a suggestion.
  useEffect(() => {
    setChoices((prev) => {
      const next: Record<string, string> = {};
      for (const p of people) {
        if (prev[p.key] !== undefined) { next[p.key] = prev[p.key]; continue; }
        const link = links.get(p.key);
        if (link) next[p.key] = link.staffId ?? IGNORE;
        else next[p.key] = suggestStaff(p.deviceName, activeStaff)?.id ?? '';
      }
      return next;
    });
  }, [people, links, activeStaff]);

  const savedChoice = (p: Person) => {
    const link = links.get(p.key);
    return link ? (link.staffId ?? IGNORE) : null;
  };
  const unsaved = people.filter((p) => choices[p.key] && choices[p.key] !== savedChoice(p));
  const unlinked = people.filter((p) => !links.has(p.key));
  const sortedPeople = useMemo(
    () => [...people].sort((a, b) =>
      Number(links.has(a.key)) - Number(links.has(b.key))
      || a.terminal.localeCompare(b.terminal)
      || Number(a.deviceUserId) - Number(b.deviceUserId)),
    [people, links],
  );

  async function saveLinks() {
    if (!unsaved.length) return;
    setSavingLinks(true);
    try {
      const now = new Date().toISOString();
      const batch = writeBatch(db);
      for (const p of unsaved) {
        const choice = choices[p.key];
        batch.set(doc(db, CLOCKING_LINKS_COLLECTION, p.key), stripUndefined({
          terminal: p.terminal,
          deviceUserId: p.deviceUserId,
          deviceName: p.deviceName,
          staffId: choice === IGNORE ? null : choice,
          updatedAt: now,
          updatedBy: actor.id,
        }));
      }
      await batch.commit();
      await recordHrAudit({ action: 'clocking.link', actor, after: { links: unsaved.length } });
    } catch (err) {
      setSaveModal({ show: true, status: 'error', message: (err as Error).message });
    } finally {
      setSavingLinks(false);
    }
  }

  /* ---------------- 3. staff-days ---------------- */

  const plan = useMemo(() => {
    // Lateness follows the school's start times (SCHOOL.clocking), the same
    // rule every screen applies. There is nothing to choose here any more:
    // a "late after" and "half day under" typed into this screen in Oct 2026
    // labelled 3,500 ordinary days half days.
    const byDay = new Map<string, { staffId: string; date: string; times: string[] }>();
    let skippedPunches = 0;
    for (const f of files) {
      for (const p of f.grid.people) {
        const link = links.get(clockingLinkId(f.terminal, p.deviceUserId));
        for (const [date, times] of Object.entries(p.days)) {
          if (!link || !link.staffId || !staffById.has(link.staffId)) { skippedPunches += times.length; continue; }
          const id = `${link.staffId}_${date}`;
          const day = byDay.get(id) ?? byDay.set(id, { staffId: link.staffId, date, times: [] }).get(id)!;
          day.times.push(...times);
        }
      }
    }
    const writes: DayWrite[] = [];
    let unchanged = 0;
    byDay.forEach((d, id) => {
      const prev = existing.get(id);
      const prevTimes = (prev?.punches ?? []).map((x) => punchHHMM(x.at));
      const merged = cleanTimes([...d.times, ...prevTimes]);
      const day = assembleGridDay(d.date, merged, clockingRules.rulesFor(staffById.get(d.staffId)?.category));
      if (prev && cleanTimes(prevTimes).join() === merged.join() && prev.status === day.status
        && prev.totalHours === day.totalHours) { unchanged += 1; return; }
      writes.push({
        id, staffId: d.staffId, date: d.date, kind: prev ? 'updated' : 'new',
        record: { staffId: d.staffId, date: d.date, ...day },
      });
    });
    // Per-person summary for the preview.
    const perStaff = new Map<string, { days: number; hours: number; late: number; noOut: number }>();
    byDay.forEach((d) => {
      const day = assembleGridDay(d.date, d.times, clockingRules.rulesFor(staffById.get(d.staffId)?.category));
      const s = perStaff.get(d.staffId) ?? perStaff.set(d.staffId, { days: 0, hours: 0, late: 0, noOut: 0 }).get(d.staffId)!;
      s.days += 1;
      s.hours += day.totalHours;
      if (day.status === 'late') s.late += 1;
      if (day.punches.length % 2 === 1) s.noOut += 1;
    });
    return { writes, unchanged, skippedPunches, perStaff, staffDays: byDay.size };
  }, [files, links, staffById, existing, clockingRules]);

  async function commit() {
    if (!plan.writes.length || !period) return;
    setImporting(true);
    setSaveModal({ show: true, status: 'saving', message: `Saving ${plan.writes.length} staff-days…` });
    try {
      const now = new Date().toISOString();
      let batch = writeBatch(db);
      let n = 0;
      for (const w of plan.writes) {
        batch.set(doc(db, HR_COLLECTIONS.attendance, w.id), stripUndefined({ ...w.record, source: 'terminal', updatedAt: now }));
        if (++n % 400 === 0) { await batch.commit(); batch = writeBatch(db); }
      }
      await batch.commit();
      const summary = {
        uploadedAt: now,
        uploadedBy: actor.id,
        uploadedByName: userData?.displayName || userData?.email || 'unknown',
        periodStart: period.start,
        periodEnd: period.end,
        files: files.map((f) => ({
          name: f.name, terminal: f.terminal, periodStart: f.grid.periodStart, periodEnd: f.grid.periodEnd,
          people: f.grid.people.length, punches: f.grid.punchCount,
        })),
        staffDays: plan.writes.length,
        newDays: plan.writes.filter((w) => w.kind === 'new').length,
        updatedDays: plan.writes.filter((w) => w.kind === 'updated').length,
        unchangedDays: plan.unchanged,
        skippedPunches: plan.skippedPunches,
      };
      await addDoc(collection(db, UPLOADS_COLLECTION), summary);
      await recordHrAudit({ action: 'clocking.import', actor, after: { ...summary, files: summary.files.length } });
      setSaveModal({
        show: true, status: 'success',
        message: `Saved ${plan.writes.length} staff-days (${summary.newDays} new, ${summary.updatedDays} updated) for ${period.start} – ${period.end}.`,
      });
      setFiles([]);
      setChoices({});
    } catch (err) {
      setSaveModal({ show: true, status: 'error', message: (err as Error).message });
    } finally {
      setImporting(false);
    }
  }

  const warnings = files.flatMap((f) => f.grid.warnings.map((w) => `${f.name}: ${w}`));
  const summaryRows = Array.from(plan.perStaff.entries())
    .map(([id, s]) => ({ id, name: staffById.get(id) ? staffFullName(staffById.get(id)!) : id, ...s }))
    .sort((a, b) => a.name.localeCompare(b.name));

  return (
    <div className="space-y-4">
      <GradientHero
        icon={Upload}
        title="Clocking upload"
        subtitle={lastUpload
          ? `Last upload ${new Date(lastUpload.uploadedAt).toLocaleDateString()} by ${lastUpload.uploadedByName} · ${lastUpload.periodStart} – ${lastUpload.periodEnd}`
          : 'No upload yet'}
        actions={onBack && (
          <AccentButton icon={ArrowLeft} variant="soft" onClick={onBack}>Clocking board</AccentButton>
        )}
      />

      <UploadReminderRecipients kind="clocking" />

      {/* 1. FILES */}
      <SoftCard hover={false}>
        <h3 className="font-semibold text-gray-800 dark:text-gray-100 flex items-center gap-2">
          <FileSpreadsheet size={16} /> 1 · Terminal exports
        </h3>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 mb-3">
          The "Attendance Record" .xls from each terminal — select both at once. Punches already in the
          portal for those dates are kept and merged, so uploading the same file twice does no harm.
        </p>
        <input
          ref={fileRef}
          type="file"
          multiple
          accept=".xls,.xlsx"
          onChange={onPick}
          className="block w-full text-sm text-gray-600 dark:text-gray-300 file:mr-3 file:py-2 file:px-3 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-[var(--accent-strong)] file:text-white hover:file:opacity-90"
        />
        {reading && <p className="text-xs text-gray-500 mt-2 flex items-center gap-1"><Loader2 size={12} className="animate-spin" /> Reading…</p>}
        {fileErrors.map((e) => <p key={e} className="text-xs text-red-600 dark:text-red-400 mt-2">{e}</p>)}

        {files.length > 0 && (
          <table className="w-full text-sm mt-3">
            <thead>
              <tr className="text-left text-xs text-gray-500 dark:text-gray-400 border-b border-gray-100 dark:border-gray-700">
                <th className="py-1.5 pr-3 font-medium">File</th>
                <th className="py-1.5 pr-3 font-medium">Terminal</th>
                <th className="py-1.5 pr-3 font-medium">Period</th>
                <th className="py-1.5 pr-3 font-medium text-right">Punches</th>
                <th />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50 dark:divide-gray-700/50">
              {files.map((f) => (
                <tr key={f.name}>
                  <td className="py-1.5 pr-3 text-gray-800 dark:text-gray-100">{f.name}</td>
                  <td className="py-1.5 pr-3">
                    <select
                      value={f.terminal}
                      onChange={(e) => setFiles((prev) => prev.map((p) => p.name === f.name ? { ...p, terminal: e.target.value } : p))}
                      className={glassFieldClass('py-1 w-28')}
                    >
                      {['1', '2', '3'].map((t) => <option key={t} value={t}>Terminal {t}</option>)}
                    </select>
                  </td>
                  <td className="py-1.5 pr-3 text-gray-600 dark:text-gray-300">{f.grid.periodStart} – {f.grid.periodEnd}</td>
                  <td className="py-1.5 pr-3 text-right tabular-nums">{f.grid.punchCount}</td>
                  <td className="py-1.5 text-right">
                    <button onClick={() => setFiles((prev) => prev.filter((p) => p.name !== f.name))} className="text-gray-400 hover:text-red-600" title="Remove">
                      <X size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {warnings.length > 0 && (
          <details className="mt-2">
            <summary className="text-xs text-amber-700 dark:text-amber-300 cursor-pointer">{warnings.length} unreadable cell{warnings.length === 1 ? '' : 's'} skipped</summary>
            {warnings.slice(0, 20).map((w) => <p key={w} className="text-xs text-gray-500 dark:text-gray-400">{w}</p>)}
          </details>
        )}
      </SoftCard>

      {/* 2. PEOPLE */}
      {people.length > 0 && (
        <SoftCard pad="p-0" hover={false}>
          <div className="px-5 py-3 border-b border-gray-100 dark:border-gray-700 flex flex-wrap items-center justify-between gap-2">
            <div>
              <h3 className="font-semibold text-gray-800 dark:text-gray-100 text-sm flex items-center gap-2">
                <Link2 size={15} /> 2 · Who is who
              </h3>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                Each terminal keeps its own ID list, so a person is linked once per terminal and remembered.
                {unlinked.length > 0 && <> <strong className="text-amber-700 dark:text-amber-300">{unlinked.length} not linked yet</strong> — their punches are left out until they are.</>}
              </p>
            </div>
            <AccentButton icon={savingLinks ? Loader2 : CheckCircle2} disabled={!unsaved.length || savingLinks} onClick={saveLinks}>
              Save {unsaved.length || ''} link{unsaved.length === 1 ? '' : 's'}
            </AccentButton>
          </div>
          <div className="overflow-x-auto max-h-[28rem]">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-white dark:bg-gray-800">
                <tr className="text-left text-xs text-gray-500 dark:text-gray-400 border-b border-gray-100 dark:border-gray-700">
                  <th className="py-2 px-4 font-medium">Terminal · ID</th>
                  <th className="py-2 px-4 font-medium">Name on terminal</th>
                  <th className="py-2 px-4 font-medium text-right">Punches</th>
                  <th className="py-2 px-4 font-medium">Staff member</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50 dark:divide-gray-700/50">
                {sortedPeople.map((p) => {
                  const choice = choices[p.key] ?? '';
                  const dirty = choice !== '' && choice !== savedChoice(p);
                  const linked = links.has(p.key);
                  return (
                    <tr key={p.key} className={dirty ? 'bg-amber-50/60 dark:bg-amber-900/10' : ''}>
                      <td className="py-1.5 px-4 text-gray-500 dark:text-gray-400 tabular-nums">T{p.terminal} · {p.deviceUserId}</td>
                      <td className="py-1.5 px-4 text-gray-800 dark:text-gray-100">
                        {p.deviceName || <span className="text-gray-400">(no name)</span>}
                        {!linked && !dirty && <span className="ml-2 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">not linked</span>}
                        {!linked && dirty && <span className="ml-2 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">suggested</span>}
                      </td>
                      <td className="py-1.5 px-4 text-right tabular-nums text-gray-600 dark:text-gray-300">{p.punches}</td>
                      <td className="py-1.5 px-4">
                        <select
                          value={choice}
                          onChange={(e) => setChoices((prev) => ({ ...prev, [p.key]: e.target.value }))}
                          className={glassFieldClass('py-1 min-w-[14rem]')}
                        >
                          <option value="">— choose —</option>
                          <option value={IGNORE}>Not staff (skip)</option>
                          {activeStaff.map((s) => <option key={s.id} value={s.id}>{staffFullName(s)}</option>)}
                        </select>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </SoftCard>
      )}

      {/* 3. IMPORT */}
      {files.length > 0 && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <StatChip tone="blue" icon={Clock} value={plan.staffDays} label="Staff-days in files" />
            <StatChip tone="emerald" value={plan.writes.filter((w) => w.kind === 'new').length} label="New" />
            <StatChip tone="amber" value={plan.writes.filter((w) => w.kind === 'updated').length} label="Gaining punches" />
            <StatChip tone={plan.skippedPunches ? 'red' : 'slate'} value={plan.skippedPunches} label="Punches skipped (unlinked)" />
          </div>

          <SoftCard pad="p-0" hover={false}>
            <div className="px-5 py-3 border-b border-gray-100 dark:border-gray-700 flex flex-wrap items-end justify-between gap-3">
              <div>
                <h3 className="font-semibold text-gray-800 dark:text-gray-100 text-sm">3 · Save to the portal</h3>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                  {plan.unchanged > 0 && `${plan.unchanged} staff-days are already stored exactly as in the files. `}
                  {!existingLoaded && 'Checking what is already stored…'}
                </p>
              </div>
              <div className="flex flex-wrap items-end gap-3">
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  Late = in after each staff type’s start time (set on the Clocking screen), in term time
                </span>
                <AccentButton
                  icon={importing ? Loader2 : CheckCircle2}
                  disabled={importing || !existingLoaded || plan.writes.length === 0}
                  onClick={commit}
                >
                  {importing ? 'Saving…' : `Save ${plan.writes.length} staff-days`}
                </AccentButton>
              </div>
            </div>
            {unlinked.length > 0 && (
              <p className="px-5 py-2 text-xs text-amber-700 dark:text-amber-300 flex items-center gap-1.5 border-b border-gray-100 dark:border-gray-700">
                <AlertTriangle size={13} /> Link everyone in step 2 first, or their punches will not be saved with this upload.
              </p>
            )}
            <div className="overflow-x-auto max-h-[28rem]">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-white dark:bg-gray-800">
                  <tr className="text-left text-xs text-gray-500 dark:text-gray-400 border-b border-gray-100 dark:border-gray-700">
                    <th className="py-2 px-4 font-medium">Staff</th>
                    <th className="py-2 px-4 font-medium text-right">Days</th>
                    <th className="py-2 px-4 font-medium text-right">Hours</th>
                    <th className="py-2 px-4 font-medium text-right">Days late</th>
                    <th className="py-2 px-4 font-medium text-right">No clock-out</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50 dark:divide-gray-700/50">
                  {summaryRows.map((r) => (
                    <tr key={r.id}>
                      <td className="py-1.5 px-4 text-gray-800 dark:text-gray-100">{r.name}</td>
                      <td className="py-1.5 px-4 text-right tabular-nums">{r.days}</td>
                      <td className="py-1.5 px-4 text-right tabular-nums">{r.hours.toFixed(1)}</td>
                      <td className="py-1.5 px-4 text-right tabular-nums">{r.late || '—'}</td>
                      <td className="py-1.5 px-4 text-right tabular-nums">{r.noOut || '—'}</td>
                    </tr>
                  ))}
                  {summaryRows.length === 0 && (
                    <tr><td colSpan={5} className="py-6 text-center text-xs text-gray-400">Nobody linked yet — complete step 2.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </SoftCard>
        </>
      )}

      {files.length === 0 && lastUpload && (
        <p className="text-xs text-gray-400 flex items-center gap-1.5"><History size={12} /> Last upload saved {lastUpload.staffDays} staff-days.</p>
      )}

      <SaveConfirmation
        show={saveModal.show}
        status={saveModal.status}
        message={saveModal.message}
        onDismiss={() => setSaveModal((m) => ({ ...m, show: false }))}
      />
    </div>
  );
}
